import axios from "axios";
import { loadTranslations, setLocale } from "react-redux-i18n";
import uuid from "uuid";

window.routePrefix = window.routePrefix || "";

/**
 * Returns a key used to control the cache for location requests
 *
 * @return {String} A valid V4 UUID
 */
const cacheControl = () => {
  return uuid.v4();
};

/**
 * Updates the route prefix
 *
 * @param {String} prefix New route prefix
 */
export const updateRoutePrefix = prefix => {
  if (typeof prefix === "string") {
    window.routePrefix = prefix;
  }
};

/**
 * This will request a json containing translations for the given
 * language and set the application locale to it so we can then localize
 * our keys.
 *
 * @param {String} language Language being requests
 * @param {function} fn Callback
 */
export const initLocale = (language = null, fn = null) => {
  return dispatch => {
    if (
      window.cancelTranslationRequest &&
      typeof window.cancelTranslationRequest === "function"
    ) {
      window.cancelTranslationRequest();
    }
    if (window.LocaleLoading === language) {
      fn && fn();
      return;
    }
    if (language) {
      window.LocaleLoading = language;
      const cacheClear = cacheControl();
      axios(
        {
          method: "GET",
          url: `${window.routePrefix}/translations/${language}.json?t=${cacheClear}`
        },
        {
          cancelToken: new axios.CancelToken(c => {
            window.cancelTranslationRequest = c; // c is the cancel function
          })
        }
      )
        .then(({ data }) => {
          window.cancelTranslationRequest = undefined;
          localStorage.setItem("last-language", language);
          dispatch(set(language, data));
          fn && fn();
        })
        .catch(error => {
          window.cancelTranslationRequest = undefined;
          if (!axios.isCancel(error)) {
            console.error("Falha ao carregar traduções", error);
            dispatch(fallback());
            fn && fn();
          }
        });
    } else {
      dispatch(fallback());
      fn && fn();
    }
  };
};

/**
 * We try to load the last language selected via localStorage,
 * if we can't, we try to use the language from the browser, and
 * as a last resort we just load portuguese translations
 *
 */
const fallback = () => {
  return dispatch => {
    if (window.LocaleLoading) return;
    let userLang = localStorage.getItem("last-language");
    userLang = userLang || navigator.language || navigator.userLanguage;
    window.LocaleLoading = userLang;
    axios({
      method: "GET",
      url: `${
        window.routePrefix
      }/translations/${userLang}.json?t=${cacheControl()}`
    })
      .then(({ data }) => {
        dispatch(set(userLang, data));
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          window.LocaleLoading = "pt-BR";
          axios({
            method: "GET",
            url: `${
              window.routePrefix
            }/translations/pt-BR.json?t=${cacheControl()}`
          }).then(({ data }) => {
            dispatch(set("pt-BR", data));
          });
        }
      });
  };
};

/**
 * Sets translations table and locale indentifier in our state
 *
 * @param {String} language Language identifier
 * @param {Object} translationKeys Translations Table
 */
const set = (language, translationKeys) => {
  return dispatch => {
    dispatch(loadTranslations(translationKeys));
    dispatch(setLocale(language));
  };
};
